import React, { useContext, useEffect, useState } from 'react';
import { ICustomer } from '@app-types/customer';
import { Button, InputController, SelectController } from '@components';
import { SelectOption } from '@components/Select';
import { StepperContext } from '@components/Stepper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUpdateCustomer, useIsMobile, useIbanBankSync, useLocalityNamesByZipCode } from '@hooks';
import { InfoIcon } from '@icons';
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { selectCustomer } from '@store/customer/selectors';
import { IBAN_MAX_LENGTH, IBAN_MIN_LENGTH, IBAN_REGEX_EU, ZIP_CODE_REGEX } from '@constants';
import { AxiosError, HttpStatusCode } from 'axios';
import { FormProvider, Resolver, ResolverOptions, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import ZipNotServedStep from './ZipNotServedStep';
import { PERSONAL_DATA_FIELDS as FIELDS } from '../constants';

type TPersonalDataForm = Pick<ICustomer, 'firstName' | 'lastName' | 'zipCode' | 'city' | 'street' | 'houseNumber' | 'iban' | 'bank'>;

const PersonalDataFormStep: React.FC = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const account = useSelector(selectCustomer);

  const { formatMessage } = useIntl();
  const { goToNextStep, goToPrevStep } = useContext(StepperContext);

  const { mutateAsync: updateCustomer } = useUpdateCustomer();

  const [formErrors, setFormErrors] = useState<string | null>(null);
  const [isZipNotServed, setZipNotServed] = useState(false);
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);

  const initialValues: TPersonalDataForm = {
    firstName: account?.firstName || '',
    lastName: account?.lastName || '',
    zipCode: account?.zipCode || '',
    city: account?.city || '',
    street: account?.street || '',
    houseNumber: account?.houseNumber || '',
    iban: account?.iban || '',
    bank: account?.bank || '',
  };

  const schema = yup.object().shape({
    firstName: yup.string().required(formatMessage({ id: 'signup.firstName.required.title' })),
    lastName: yup.string().required(formatMessage({ id: 'signup.lastName.required.title' })),
    zipCode: yup
      .string()
      .required(formatMessage({ id: 'signup.zipCode.required.title' }))
      .matches(ZIP_CODE_REGEX, formatMessage({ id: 'signup.zipCode.invalid.title' })),
    city: yup.string().required(formatMessage({ id: 'signup.city.required.title' })),
    street: yup.string().required(formatMessage({ id: 'signup.street.required.title' })),
    houseNumber: yup.string().required(formatMessage({ id: 'signup.houseNumber.required.title' })),
    iban: yup
      .string()
      .required(formatMessage({ id: 'signup.iban.required.title' }))
      .matches(IBAN_REGEX_EU, formatMessage({ id: 'signup.iban.invalid.title' }))
      .test('iban-length', formatMessage({ id: 'signup.iban.invalid.title' }), (value) => {
        const len = value?.replace(/\s/g, '').length ?? 0;
        return len >= IBAN_MIN_LENGTH && len <= IBAN_MAX_LENGTH;
      }),
    bank: yup.string().required(formatMessage({ id: 'signup.bank.required.title' })),
  });

  const methods = useForm<TPersonalDataForm>({
    resolver: yupResolver(schema) as unknown as Resolver<TPersonalDataForm, ResolverOptions<TPersonalDataForm>>,
    defaultValues: initialValues,
  });

  const { control, watch, handleSubmit, formState, reset }  = methods;
  const { errors, dirtyFields, isDirty, isSubmitting } = formState;

  const zipCode = watch('zipCode');
  const { data: localityNamesData } = useLocalityNamesByZipCode(zipCode);

  useEffect(() => {
    const localityNames = localityNamesData?.localityNames || [];
  
    if (!localityNames.length) {
      setCityOptions([]);
      return;
    }
    
    const options = localityNames.map((localityName) => ({ value: localityName, label: localityName }));
    setCityOptions(options);

    if (dirtyFields.zipCode) {
      methods.setValue('city', localityNames[0], { shouldValidate: true, shouldDirty: true });
    } else {
      methods.setValue('city', account?.city || '', { shouldValidate: true, shouldDirty: true });
    }
  }, [localityNamesData?.localityNames]);

  useIbanBankSync(watch('iban') ?? '', (name, value, opts) => methods.setValue(name, value, opts), () => methods.trigger('iban'));

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');

    if (value.length <= 5) {
      methods.setValue('zipCode', value, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleFormSubmit = async (data: TPersonalDataForm) => {
    if (!account)
      return;

    data.iban = data.iban.replace(/\s/g, '');

    try {
      const res = await updateCustomer({ id: account.id, customer: data });

      if (res.status === HttpStatusCode.NotFound) {
        reset();
        setZipNotServed(true);
        return;
      }

      if (res instanceof AxiosError) 
        return setFormErrors(formatMessage({ id: 'preparingForNextSubmission.step.2.2.form.error.common' }));

      goToNextStep();
    } catch (error) {
      if (error instanceof AxiosError) 
        return setFormErrors(formatMessage({ id: 'preparingForNextSubmission.step.2.2.form.error.common' }));

      console.error('Error:', error);
    }
  };

  const renderField = (field: string) => {
    const commonProps = {
      fullWidth: true,
      name: field,
      control,
      placeholder: formatMessage({ id: `signup.${field}.placeholder` }),
      label: formatMessage({ id: `signup.${field}.label` }),
      error: !!errors[field as keyof TPersonalDataForm],
      helperText: errors[field as keyof TPersonalDataForm]?.message,
      backgroundMode: 'white' as const,
    };

    if (field === 'zipCode') {
      return (
        <InputController
          {...commonProps}
          onChange={handleZipCodeChange}
          number
        />
      );
    }

    if (field === 'city') {
      return cityOptions.length > 1 ? (
        <SelectController
          {...commonProps}
          options={cityOptions}
        />
      ) : (
        <InputController
          {...commonProps}
          disabled={!!cityOptions.length}
        />
      );
    }

    return <InputController {...commonProps} />;
  };

  if (!account)
    return;

  if (isZipNotServed)
    return <ZipNotServedStep onBack={() => setZipNotServed(false)} />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack maxWidth={890} alignItems="center" gap={4} sx={{ my: theme.spacing(5) }}>
          <Typography variant="h2" textAlign="center">
            <FormattedMessage id="preparingForNextSubmission.step.2.2.title" />
          </Typography>
          <Stack 
            sx={{
              alignItems: 'center',
              padding: isMobile ? '22px 10px' : '34px 56px 40px 56px',
              borderRadius: '8px',
              backgroundColor: theme.palette.grey['600'],
            }}
          >
            <Grid container xs={12} spacing={3}>
              {FIELDS.map((field) => (
                <Grid key={field} item xs={12} sm={6}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>

            {formErrors && (
              <Box sx={{ display: 'flex', gap: 1, mt: 5 }}>
                <Box sx={{ flexShrink: 0 }}>
                  <InfoIcon color={theme.palette.error.main} />
                </Box>
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: theme.palette.grey[200],
                      '& > span': { fontWeight: '600', color: theme.palette.grey[200] },
                    }}
                    dangerouslySetInnerHTML={{ __html: formErrors }}
                  />
                </Box>
              </Box>
            )}
  
          </Stack>
          <Stack direction="row" gap={isMobile ? 2 : 3}>
            <Button variant="outlined" onClick={goToPrevStep}>
              <FormattedMessage id="common.back" />
            </Button>
            <Button type="submit" variant="contained" disabled={!isDirty || isSubmitting}>
              <FormattedMessage id="common.save" />
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default PersonalDataFormStep;
